import React from 'react';
import moment from 'moment';
import { metricKeys } from '@pulse/metrics';
import { Tooltip } from 'antd';
import { ColumnShape } from 'react-base-table';
import { ExclamationCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { getMetricShortName } from '@utils/metrics';
import { SERVICE_INT_DATE_FORMAT } from '@utils/constants';
import { DESC } from '@utils/table/constants';
import HeaderCell from './HeaderCell';
import ValueCell from './ValueCell';
import LinkTitleCell from './LinkTitleCell';
import { OutlierPriority } from './types';

const ACTIVE_TAB = {
  key: 'active',
  displayName: 'Active',
};

const ARCHIVED_TAB = {
  key: 'archived',
  displayName: 'Archived',
};

export const AUTO_SCAN_TABS = [ACTIVE_TAB, ARCHIVED_TAB];

export const OUTLIER_TYPE = 'OutlierFeedEntity';

export const TYPE_OPTIONS = [
  { value: OUTLIER_TYPE, label: 'Outliers' },
  { value: 'BrokenAssetsEntity', label: 'Broken Assets' },
  { value: 'NewAppVersionEntity', label: 'New App Version' },
  { value: 'AiAlertInsights', label: 'AI Alert Insights' },
  { value: 'UnderperformingCohorts', label: 'Underperforming Cohorts' },
];

export const PRIORITY: { [key: string]: OutlierPriority } = {
  high: 'HIGH',
  medium: 'MEDIUM',
  low: 'LOW',
};

export const defaultQuerySortBy = {
  field: 'lastDetected',
  order: DESC,
};

export const OUTLIER_METRICS = [
  metricKeys.StreamingPerformanceIndex,
  metricKeys.SpiStreams,
  metricKeys.BadSession,
  // metricKeys.BadUniqueDevices, // this is coming destructured in data item object now, might add here later
];

export const OUTLIER_METRICS_WITH_POTENTIAL_IMPROVEMENT = [metricKeys.StreamingPerformanceIndex];

export const PRIORITY_MAPPING = {
  [PRIORITY.low]: {
    title: 'Low',
    icon: (
      <InfoCircleOutlined style={{ color: '#788BA9', marginRight: '3px', paddingTop: '4px' }} />
    ),
  },
  [PRIORITY.medium]: {
    title: 'Medium',
    icon: <WarningOutlined style={{ color: '#FF9300', marginRight: '3px', paddingTop: '4px' }} />,
  },
  [PRIORITY.high]: {
    title: 'High',
    icon: (
      <ExclamationCircleOutlined
        style={{ color: '#D0021B', marginRight: '3px', paddingTop: '4px' }}
      />
    ),
  },
};

export const dimensionSearchFn = ({ searchText, dimensionList, data, columnKey }) => {
  const regex = new RegExp(searchText, 'i'); // ignore case
  return data.filter((row) => {
    const dimensionKeyValuePair = Object.entries(row?.[columnKey])[0] as [string, { name: string }];

    return (
      regex.test(dimensionKeyValuePair[0]) ||
      regex.test(dimensionList[dimensionKeyValuePair[0]]) ||
      regex.test(dimensionKeyValuePair[1].name)
    );
  });
};

export const getPotentialImprovementMetricKey = (metricKey: string) => {
  return `potentialMetricImprovement.${metricKey}`;
};

const headerRenderer = (title, withTooptip, tooltipText) =>
  withTooptip ? (
    <span>
      {title}
      <Tooltip title={tooltipText}>
        <InfoCircleOutlined style={{ fontSize: 12, verticalAlign: 'super' }} />
      </Tooltip>
    </span>
  ) : (
    <span>{title}</span>
  );

const getMetricColumns = (isColumnSortable: boolean) => {
  const metricColumns: ColumnShape[] = [];
  OUTLIER_METRICS.map((metricKey) => {
    const title = getMetricShortName({ metricKey, hasSubscript: false });
    const shortTitle = getMetricShortName({ metricKey, hasSubscript: false });
    const potentialImprovementTitle = `Potential ${shortTitle} Improvement`;
    metricColumns.push({
      title,
      dataKey: 'metrics',
      key: metricKey,
      metric: metricKey,
      width: 140,
      align: 'right',
      sortable: isColumnSortable,
      headerRenderer: () =>
        headerRenderer(
          title,
          metricKey === metricKeys.StreamingPerformanceIndex,
          'Calculated on KPI: Conviva Good',
        ),
      cellRenderer: ({ cellData, column }) => {
        return <ValueCell cellData={cellData[metricKey]} column={column} />;
      },
    });

    if (OUTLIER_METRICS_WITH_POTENTIAL_IMPROVEMENT.includes(metricKey)) {
      metricColumns.push({
        title: potentialImprovementTitle,
        dataKey: 'potentialMetricImprovement',
        key: getPotentialImprovementMetricKey(metricKey),
        metric: metricKey,
        width: 240,
        align: 'right',
        colorless: true,
        sortable: true, // sorting on this collumn allowed in both tabs for now
        headerRenderer: () =>
          headerRenderer(
            potentialImprovementTitle,
            metricKey === metricKeys.StreamingPerformanceIndex,
            'The calculated impact to All Traffic SPI Conviva Good if the outlier were improved',
          ),
        cellRenderer: ({ cellData, column }) => (
          <ValueCell cellData={cellData[metricKey] ? cellData[metricKey] : 'NA'} column={column} />
        ),
      });
    }
  });

  return metricColumns;
};

export const getOutliersTableColumns = ({ tab, dimensionSearchProps }) => {
  const isColumnSortable = tab !== ARCHIVED_TAB.key;

  // BE sorting for archived tab, available for firstDetected, impactedUniqueDevices, lastDetected, priority, status
  const outlierColumns = [
    {
      title: 'Improvement Areas',
      dataKey: 'priority',
      key: 'priority', // improvement area column will be sorted on priority
      width: 450,
      resizable: true,
      sortable: true,
      searchProps: dimensionSearchProps,
      headerRenderer: (props) => <HeaderCell {...props} />,
      cellRenderer: ({ cellData, rowData, column }) => (
        <LinkTitleCell cellData={cellData} rowData={rowData} column={column} />
      ),
    },
    ...getMetricColumns(isColumnSortable),
    {
      title: 'Impacted Devices',
      dataKey: 'impactedUniqueDevices',
      key: 'impactedUniqueDevices',
      metric: metricKeys.BadUniqueDevices,
      width: 150,
      align: 'right',
      sortable: true,
      cellRenderer: ({ cellData, column }) => {
        return <ValueCell cellData={cellData} column={column} />;
      },
    },
    // TODO: will be added in next phases
    // {
    //   title: 'Impacted Devices(/hr)',
    //   dataKey: 'impactedUniqueDevicesperHour',
    //   key: 'impactedUniqueDevicesperHour',
    //   metric: 'impactedUniqueDevicesperHour',
    //   width: 150,
    //   sortable: isColumnSortable,
    //   cellRenderer: ({ cellData, column }) => {
    //     return <ValueCell cellData={cellData} column={column} />;
    //   },
    // },
    {
      title: 'First Detected',
      dataKey: 'firstDetected',
      key: 'firstDetected',
      width: 200,
      align: 'right',
      sortable: true,
      cellRenderer: ({ cellData }) => moment(cellData).format(SERVICE_INT_DATE_FORMAT),
    },
    {
      title: 'Last Detected',
      dataKey: 'lastDetected',
      key: 'lastDetected',
      width: 200,
      align: 'right',
      sortable: true,
      cellRenderer: ({ cellData }) => moment(cellData).format(SERVICE_INT_DATE_FORMAT),
    },
  ];

  return outlierColumns;
};
